// @flow
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import i18NextDomainDetector from '@alphaott/i18next-domain-detector'

// ToDo: load locales from CDN https://github.com/i18next/i18next-xhr-backend
import { ru, en, he, es, pt, fr, el, ro } from '@alphaott/locales'

const isDev: boolean = process.env.NODE_ENV === 'development'

const languageDetector = new LanguageDetector()
languageDetector.addDetector(i18NextDomainDetector)

/**
 * Detector Options
 * https://github.com/i18next/i18next-browser-languageDetector#detector-options
 */
const detection = {
  caches: ['localStorage'],
  order: [
    'querystring',
    'cookie',
    'localStorage',
    'domain',
    'navigator',
    'htmlTag',
    'path',
    'subdomain',
  ],
}

// Smart TV texts missing from the shared locales. Added without overwriting:
// a key that appears in the shared locales later takes precedence
export const SMART_TV_RESOURCES = {
  en: {
    mwstv_player_retry: 'Retry',
    mwstv_player_stream_error: 'Stream error occurred. Please try again later.',
    mwstv_player_stream_error_code:
      'Stream error occurred. Error code: {{error}}. Please try again later.',
  },
  ru: {
    mwstv_player_retry: 'Повторить',
    mwstv_player_stream_error: 'Ошибка воспроизведения. Попробуйте ещё раз позже.',
    mwstv_player_stream_error_code:
      'Ошибка воспроизведения. Код ошибки: {{error}}. Попробуйте ещё раз позже.',
    close: 'Закрыть',
  },
  he: {
    mwstv_player_retry: 'נסה שוב',
    mwstv_player_stream_error: 'אירעה שגיאה בהפעלה. נסו שוב מאוחר יותר.',
    mwstv_player_stream_error_code: 'אירעה שגיאה בהפעלה. קוד שגיאה: {{error}}. נסו שוב מאוחר יותר.',
    close: 'סגירה',
  },
  es: {
    mwstv_player_retry: 'Reintentar',
    mwstv_player_stream_error: 'Se produjo un error de reproducción. Inténtelo de nuevo más tarde.',
    mwstv_player_stream_error_code:
      'Se produjo un error de reproducción. Código de error: {{error}}. Inténtelo de nuevo más tarde.',
  },
  pt: {
    mwstv_player_retry: 'Tentar novamente',
    mwstv_player_stream_error: 'Ocorreu um erro de reprodução. Tente novamente mais tarde.',
    mwstv_player_stream_error_code:
      'Ocorreu um erro de reprodução. Código de erro: {{error}}. Tente novamente mais tarde.',
  },
  fr: {
    mwstv_player_retry: 'Réessayer',
    mwstv_player_stream_error: 'Une erreur de lecture est survenue. Veuillez réessayer plus tard.',
    mwstv_player_stream_error_code:
      "Une erreur de lecture est survenue. Code d'erreur : {{error}}. Veuillez réessayer plus tard.",
  },
  el: {
    mwstv_player_retry: 'Δοκιμάστε ξανά',
    mwstv_player_stream_error: 'Παρουσιάστηκε σφάλμα αναπαραγωγής. Δοκιμάστε ξανά αργότερα.',
    mwstv_player_stream_error_code:
      'Παρουσιάστηκε σφάλμα αναπαραγωγής. Κωδικός σφάλματος: {{error}}. Δοκιμάστε ξανά αργότερα.',
  },
  ro: {
    mwstv_player_retry: 'Reîncercați',
    mwstv_player_stream_error: 'A apărut o eroare de redare. Încercați din nou mai târziu.',
    mwstv_player_stream_error_code:
      'A apărut o eroare de redare. Cod de eroare: {{error}}. Încercați din nou mai târziu.',
  },
}

const addSmartTVResources = () => {
  Object.keys(SMART_TV_RESOURCES).forEach((language) => {
    i18n.addResourceBundle(language, 'common', SMART_TV_RESOURCES[language], true, false)
  })
}

/**
 * ISO 639
 * https://www.iso.org/iso-639-language-codes.html
 * http://www.loc.gov/standards/iso639-2/php/code_list.php
 */
// eslint-disable-next-line complexity
export const configureI18n = (config: Object) => {
  const initialized = i18n
    // .use(Locize)
    .use(initReactI18next) // passes i18n down to react-i18next
    .use(languageDetector)
    .init({
      ns: ['common'],
      defaultNS: 'common',

      resources: { en, ru, he, es, pt, fr, el, ro },

      lng: localStorage.getItem('i18nextLng') ? null : 'en',
      fallbackLng: 'en',

      nsSeparator: '::',
      keySeparator: '.',

      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },

      debug: isDev,
      saveMissing: isDev, // calls save missing key function on backend if key not found

      detection,

      ...(config.lng && {
        lng: config.lng,
      }),
      ...(config.supportedLngs && {
        supportedLngs: config.supportedLngs,
      }),
    })

  addSmartTVResources()

  return initialized
}

export default configureI18n
